import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const LoginPage = () => {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setNotice(data.message || "If an account exists for this email, a reset link is on its way.");
        toast.success("If the account exists, a reset link is on its way.");
      } else {
        const user =
          mode === "login"
            ? await login(form.email, form.password)
            : await register(form.name, form.email, form.password);
        toast.success(mode === "login" ? `Welcome back, ${user.name}!` : "Account created — welcome aboard!");
        navigate(user.role === "admin" ? "/admin" : "/my-reservations");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-5 py-16 hero-grain relative" data-testid="login-page">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--brand)]/10 blur-3xl pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <button
          data-testid="login-back-home-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-3 mx-auto mb-8 group"
        >
          <img src="/logo.png" alt="Voyage Switzerland" className="h-20 w-auto" />
        </button>

        <div className="rounded-[2rem] bg-white border border-slate-100 shadow-[0_30px_70px_-30px_rgba(11,19,43,0.25)] p-7 sm:p-9">
          {mode === "forgot" ? (
            <div className="mb-7 text-center">
              <h2 className="font-display font-bold text-xl text-[#0b132b]" data-testid="forgot-title">Reset your password</h2>
              <p className="mt-1.5 text-sm text-slate-500">Enter your account email and we'll send you a reset link.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 rounded-full bg-slate-100 p-1.5 mb-7" data-testid="login-mode-tabs">
              {[
                { id: "login", label: "Sign In" },
                { id: "register", label: "Create Account" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`login-tab-${t.id}`}
                  onClick={() => {
                    setMode(t.id);
                    setError("");
                    setNotice("");
                  }}
                  className={`rounded-full py-2.5 text-sm font-bold transition-all duration-300 ${
                    mode === t.id ? "bg-[var(--brand)] text-white shadow-lg shadow-[var(--brand)]/30" : "text-slate-500 hover:text-[#0b132b]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {mode === "register" && (
              <input
                data-testid="login-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className={inputCls}
              />
            )}
            <input
              data-testid="login-email-input"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Email address"
              className={inputCls}
            />
            {mode !== "forgot" && (
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Password"
                  className={`${inputCls} pr-12`}
                />
                <button
                  type="button"
                  data-testid="login-toggle-password-button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[var(--brand)] transition-colors"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
            )}
            {mode === "login" && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  data-testid="login-forgot-password-link"
                  onClick={() => {
                    setMode("forgot");
                    setError("");
                    setNotice("");
                  }}
                  className="text-xs font-bold text-[var(--brand)] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <p className="text-sm font-semibold text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="login-error-message">
                {error}
              </p>
            )}
            {notice && (
              <p className="text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="forgot-success-message">
                {notice}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
            >
              {busy ? "One moment…" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
            </button>
          </form>

          {mode === "forgot" ? (
            <button
              data-testid="forgot-back-to-login-button"
              onClick={() => {
                setMode("login");
                setError("");
                setNotice("");
              }}
              className="mt-5 mx-auto flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[var(--brand)] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
            </button>
          ) : (
            <p className="mt-5 text-center text-xs text-slate-400 font-medium">
              {mode === "login" ? "New here? Create an account to book faster." : "Already have an account? Switch to Sign In."}
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};
